import { useState, useEffect } from "react";
import { FiEdit2, FiTrash2, FiRefreshCw, FiPlus } from "react-icons/fi";
import { toast } from "react-toastify";
import BulkRuleModal from "../modals/BulkRuleModal";
import {
  fetchBulkRules,
  createBulkRule,
  updateBulkRule,
  deleteBulkRule,
} from "../api/costingSettingsApi";
import { FaEdit } from "react-icons/fa";

// ─── Constants ────────────────────────────────────────────────────────────

const defaultForm = {
  category: "",
  grossMarginPercent: "",
  webPercent: "",
  wholesale1Percent: "",
  wholesale2Percent: "",
  distributorPercent: "",
  exportPercent: "",
  exportMenaPercent: "",
};

const TABLE_HEADERS = [
  "Category",
  "Gross Margin %",
  "Web %",
  "Wholesale 1 %",
  "Wholesale 2 %",
  "Distributor %",
  "Export %",
  "Export MENA %",
  "Action",
];

// ─── Component ────────────────────────────────────────────────────────────

export default function BulkRule() {
  const [rules, setRules] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);

  // ─── API handlers ──────────────────────────────────────────────────────

  const loadRules = async () => {
    setTableLoading(true);
    try {
      const data = await fetchBulkRules();
      setRules(data);
    } catch (err) {
      toast.error("Failed to fetch rules");
    } finally {
      setTableLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitLoading(true);
    try {
      if (editingId) {
        await updateBulkRule(editingId, form);
        toast.success("Rule updated successfully!");
      } else {
        await createBulkRule(form);
        toast.success("Rule created successfully!");
      }
      setShowModal(false);
      setForm(defaultForm);
      setEditingId(null);
      loadRules();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Operation failed");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (id) => {

    try {
      await deleteBulkRule(id);
      toast.success("Rule deleted!");
      loadRules();
    } catch (err) {
      console.error("delete error:", err);
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  // ─── Modal openers ─────────────────────────────────────────────────────

  const openAdd = () => {
    setEditingId(null);
    setForm(defaultForm);
    setShowModal(true);
  };

  const openEdit = (rule) => {
    setEditingId(rule._id);
    setForm({
      category: rule.category || "",
      grossMarginPercent: rule.grossMarginPercent ?? "",
      webPercent: rule.webPercent ?? "",
      wholesale1Percent: rule.wholesale1Percent ?? "",
      wholesale2Percent: rule.wholesale2Percent ?? "",
      distributorPercent: rule.distributorPercent ?? "",
      exportPercent: rule.exportPercent ?? "",
      exportMenaPercent: rule.exportMenaPercent ?? "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (submitLoading) return;
    setShowModal(false);
    setEditingId(null);
    setForm(defaultForm);
  };

  // ─── Lifecycle ─────────────────────────────────────────────────────────

  useEffect(() => {
    loadRules();
  }, []);

  // ─── Render ────────────────────────────────────────────────────────────

  return (
     <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-10 mb-10">

    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

      {/* Header */}
      <div className="flex flex-row justify-between items-start px-6 py-5 border-b border-gray-100">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Bulk Pricing
          </h1>
          <p className="text-sm text-gray-600 mt-2">
            Define channel-specific margin structures to standardize bulk sales pricing.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={loadRules}
            disabled={tableLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200
              text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <FiRefreshCw className={`w-4 h-4 ${tableLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>

          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-900 text-white
              text-sm font-medium hover:bg-gray-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Add New Rule
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              {TABLE_HEADERS.map((h) => (
                <th key={h} className="px-6 py-4 text-left font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {tableLoading ? (
              <tr>
                <td colSpan={TABLE_HEADERS.length} className="text-center py-14 text-gray-400">
                  <FiRefreshCw className="animate-spin w-6 h-6 mx-auto mb-2" />
                  Loading...
                </td>
              </tr>
            ) : rules.length === 0 ? (
              <tr>
                <td colSpan={TABLE_HEADERS.length} className="text-center py-14 text-gray-400">
                  No rules found. Click "Add New Rule" to get started.
                </td>
              </tr>
            ) : (
              rules.map((rule) => (
                <tr
                  key={rule._id}
                  className="border-t border-gray-200 hover:bg-gray-50"
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{rule.category}</td>
                  {/* <td className="px-4 py-3">{rule.grossMarginPercent}%</td>
                  <td className="px-4 py-3">{rule.webPercent}%</td>
                  <td className="px-4 py-3">{rule.wholesale1Percent}%</td>
                  <td className="px-4 py-3">{rule.wholesale2Percent}%</td>
                  <td className="px-4 py-3">{rule.distributorPercent}%</td>
                  <td className="px-4 py-3">{rule.exportPercent}%</td>
                  <td className="px-4 py-3">{rule.exportMenaPercent}%</td> */}
                  <td className="px-4 py-3">
  {Number(rule.grossMarginPercent || 0).toFixed(2)}%
</td>

<td className="px-4 py-3">
  {Number(rule.webPercent || 0).toFixed(2)}%
</td>

<td className="px-4 py-3">
  {Number(rule.wholesale1Percent || 0).toFixed(2)}%
</td>

<td className="px-4 py-3">
  {Number(rule.wholesale2Percent || 0).toFixed(2)}%
</td>

<td className="px-4 py-3">
  {Number(rule.distributorPercent || 0).toFixed(2)}%
</td>

<td className="px-4 py-3">
  {Number(rule.exportPercent || 0).toFixed(2)}%
</td>

<td className="px-4 py-3">
  {Number(rule.exportMenaPercent || 0).toFixed(2)}%
</td>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(rule)}
                        className="p-1.5 text-blue-500 hover:bg-blue-100 rounded-lg"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleDelete(rule._id)}
                        className="p-1.5 text-red-500 hover:bg-red-100 rounded-lg"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>

    {/* ✅ Modal MUST be inside root */}
    <BulkRuleModal
      isOpen={showModal}
      onClose={closeModal}
      onSubmit={handleSubmit}
      form={form}
      setForm={setForm}
      editingId={editingId}
      loading={submitLoading}
    />
  </div>
  );
}